// API functions for the self-hosted customer survey. These hit the public
// (no-auth) endpoints, so we swap the /v1/client base for /v1/public.
const serverURL = import.meta.env.VITE_API_URL || ''
const publicURL = serverURL.replace('/v1/client', '/v1/public')

export type SurveyStatus = 'open' | 'submitted'

export interface SurveyState {
  status: SurveyStatus
}

export interface SurveySubmission {
  likes?: string
  improvements?: string
  received_funding?: boolean | null
  received_funding_details?: string
  rating?: number | null
  is_anonymous: boolean
  contact_opt_in: boolean
}

/**
 * Resolve a survey link. Returns its state ('open' | 'submitted'), or
 * 'notfound' when the uuid is unknown (404).
 */
export async function getSurveyState(
  uuid: string,
): Promise<SurveyState | 'notfound'> {
  const response = await fetch(`${publicURL}/survey/${uuid}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (response.status === 404) return 'notfound'
  if (!response.ok) throw new Error('Failed to load survey')
  return response.json()
}

/**
 * Submit a survey. Returns 'submitted' on success — or on a 409 (already
 * submitted), which the page treats the same way — and 'notfound' on a 404.
 */
export async function submitSurvey(
  uuid: string,
  submission: SurveySubmission,
): Promise<'submitted' | 'notfound'> {
  const response = await fetch(`${publicURL}/survey/${uuid}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(submission),
  })
  if (response.status === 404) return 'notfound'
  if (response.ok || response.status === 409) return 'submitted'
  throw new Error('Failed to submit survey')
}
